import type { LoaderFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { podcastAPI, episodeAPI, subscriberAPI, APIError } from "../../lib/api";
import type { SubscribersPageData } from "../../pages/podcasts/SubscribersPage";
import type { PodcastResponse, EpisodeResponse } from "../../types/api";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

export interface PodcastListData {
  podcasts: PodcastResponse[];
  organizationId: string;
}

export interface PodcastDetailData {
  podcast: PodcastResponse;
  organizationId: string;
}

export async function podcastListLoader({ params }: LoaderFunctionArgs): Promise<PodcastListData> {
  const { id: orgId } = params;

  if (!orgId || !isValidUUID(orgId)) {
    throw redirect("/organizations");
  }

  try {
    const podcasts = await podcastAPI.getAll(orgId);
    return { podcasts, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect("/organizations");
    }
    throw error;
  }
}

export async function podcastDetailLoader({
  params,
}: LoaderFunctionArgs): Promise<PodcastDetailData> {
  const { id: orgId, podcastId } = params;

  if (!orgId || !podcastId || !isValidUUID(orgId) || !isValidUUID(podcastId)) {
    throw redirect("/organizations");
  }

  try {
    const podcast = await podcastAPI.getById(orgId, podcastId);
    return { podcast, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/podcasts`);
    }
    throw error;
  }
}

export interface CreatePodcastLoaderData {
  organizationId: string;
}

export async function createPodcastLoader({
  params,
}: LoaderFunctionArgs): Promise<CreatePodcastLoaderData> {
  const { id: orgId } = params;

  if (!orgId || !isValidUUID(orgId)) {
    throw redirect("/organizations");
  }

  return { organizationId: orgId };
}

export interface EpisodeListData {
  episodes: EpisodeResponse[];
  podcast: PodcastResponse;
  organizationId: string;
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

const PAGE_SIZE = 20;

export async function episodeListLoader({
  params,
  request,
}: LoaderFunctionArgs): Promise<EpisodeListData> {
  const { id: orgId, podcastId } = params;

  if (!orgId || !podcastId || !isValidUUID(orgId) || !isValidUUID(podcastId)) {
    throw redirect("/organizations");
  }

  const url = new URL(request.url);
  const status = url.searchParams.get("status") || undefined;
  const page = parseInt(url.searchParams.get("page") || "1", 10);
  const offset = (page - 1) * PAGE_SIZE;

  try {
    const [podcast, episodes] = await Promise.all([
      podcastAPI.getById(orgId, podcastId),
      episodeAPI.getAll(orgId, podcastId, { status, limit: PAGE_SIZE, offset }),
    ]);

    return {
      episodes,
      podcast,
      organizationId: orgId,
      pagination: {
        total: podcast.episodeCount ?? episodes.length,
        limit: PAGE_SIZE,
        offset,
      },
    };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/podcasts`);
    }
    throw error;
  }
}

export interface CreateEpisodeLoaderData {
  podcast: PodcastResponse;
  organizationId: string;
}

export async function createEpisodeLoader({
  params,
}: LoaderFunctionArgs): Promise<CreateEpisodeLoaderData> {
  const { id: orgId, podcastId } = params;

  if (!orgId || !podcastId || !isValidUUID(orgId) || !isValidUUID(podcastId)) {
    throw redirect("/organizations");
  }

  try {
    const podcast = await podcastAPI.getById(orgId, podcastId);
    return { podcast, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/podcasts`);
    }
    throw error;
  }
}

export interface EpisodeEditorData {
  episode: EpisodeResponse;
  podcast: PodcastResponse;
  organizationId: string;
}

export async function episodeEditorLoader({
  params,
}: LoaderFunctionArgs): Promise<EpisodeEditorData> {
  const { id: orgId, podcastId, episodeId } = params;

  if (
    !orgId ||
    !podcastId ||
    !episodeId ||
    !isValidUUID(orgId) ||
    !isValidUUID(podcastId) ||
    !isValidUUID(episodeId)
  ) {
    throw redirect("/organizations");
  }

  try {
    const [podcast, episode] = await Promise.all([
      podcastAPI.getById(orgId, podcastId),
      episodeAPI.getById(orgId, podcastId, episodeId),
    ]);
    return { episode, podcast, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/podcasts/${podcastId}`);
    }
    throw error;
  }
}

export async function subscribersLoader({
  params,
}: LoaderFunctionArgs): Promise<SubscribersPageData> {
  const { id: orgId, podcastId } = params;

  if (!orgId || !podcastId || !isValidUUID(orgId) || !isValidUUID(podcastId)) {
    throw redirect("/organizations");
  }

  try {
    const [podcast, subscribersResult, stats] = await Promise.all([
      podcastAPI.getById(orgId, podcastId),
      subscriberAPI.list(orgId, podcastId, { limit: 50, offset: 0 }),
      subscriberAPI.getStats(orgId, podcastId),
    ]);
    return {
      podcast,
      organizationId: orgId,
      initialSubscribers: subscribersResult.subscribers,
      initialTotal: subscribersResult.total,
      stats,
    };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/podcasts`);
    }
    throw error;
  }
}
